import React, { useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { useTranslation } from 'react-i18next';

import { PlaylistItem, videoDetails } from 'packages/common/types/playlist';
import { useTheme } from '../../../../../ThemeContext';
import styles from './CollapsibleText.module.scss';

type Props = {
  text: string;
  className?: string;
  maxHeight?: 'none' | number;
  maxWords?: number;
  maxLines?: number;
  showMoreClickHandler?: () => void;
  isHubContent?: boolean;
  item: PlaylistItem | videoDetails;
};

const CollapsibleText: React.FC<Props> = ({
  text,
  item,
  className,
  maxHeight = 'none',
  maxWords = 30,
  maxLines,
  showMoreClickHandler,
  isHubContent,
}: Props) => {
  const { t } = useTranslation('common');
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  const getTruncatedText = (inputText: string, wordLimit: number) => {
    const words = inputText.split(' ');
    const isTrancated = words.length > wordLimit;
    const truncated = words.length > wordLimit ? words.slice(0, wordLimit).join(' ') + '...' : inputText;
    return { truncated, isTrancated };
  };
  const { universalStyles, themeConfig } = useTheme();
  const safeText = text ?? '';
  const hasText = safeText.trim() === '';

  const { truncated: wordTruncated, isTrancated } = getTruncatedText(safeText, maxWords);
  const ismaxWordsExceeds = !hasText ? safeText.split(' ').length > maxWords : false;
  const isShowMoreButton = (isHubContent && hasText) || (isHubContent && !isTrancated);
  const isShowMoreIsmaxWordsExceeds = isHubContent && isTrancated;

  const [lineTruncated, setLineTruncated] = useState(safeText);
  const [isLineOverflow, setIsLineOverflow] = useState(false);

  const allEmpty = ([item?.audio_language, item?.sub_title, item?.cast, item?.director, item?.producer, item?.studio] as (string | undefined | null)[]).every(
    (value) => !value || value.trim() === '',
  );

  const { color, ...textStyle } = universalStyles.videoDetailsDescription || {};

  useLayoutEffect(() => {
    if (!maxLines) {
      return;
    }

    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) {
      return;
    }

    const moreLabel = t('more');

    const measureOverflow = () => {
      if (container.clientWidth <= 0) {
        return;
      }

      const sourceStyle = getComputedStyle(container);
      measure.style.width = `${container.clientWidth}px`;
      measure.style.fontSize = sourceStyle.fontSize;
      measure.style.fontFamily = sourceStyle.fontFamily;
      measure.style.fontWeight = sourceStyle.fontWeight;
      measure.style.lineHeight = sourceStyle.lineHeight;
      measure.style.letterSpacing = sourceStyle.letterSpacing;
      measure.style.whiteSpace = 'pre-wrap';

      const fits = (content: string, withMore: boolean) => {
        measure.textContent = content;
        if (withMore) {
          const more = document.createElement('span');
          more.textContent = ` ${moreLabel}`;
          measure.appendChild(more);
        }
        const computed = getComputedStyle(measure);
        const lineHeight = Number.parseFloat(computed.lineHeight) || Number.parseFloat(computed.fontSize) * 1.4;
        return measure.scrollHeight <= lineHeight * maxLines + 1;
      };

      if (fits(safeText, false)) {
        setLineTruncated(safeText);
        setIsLineOverflow(false);
        return;
      }

      const words = safeText.split(/\s+/).filter(Boolean);
      let low = 0;
      let high = words.length;
      let best = 1;

      while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        const candidate = `${words.slice(0, mid).join(' ')}...`;
        if (fits(candidate, true)) {
          best = mid;
          low = mid + 1;
        } else {
          high = mid - 1;
        }
      }

      setLineTruncated(`${words.slice(0, Math.max(best, 1)).join(' ')}...`);
      setIsLineOverflow(true);
    };

    measureOverflow();
    const observer = new ResizeObserver(measureOverflow);
    observer.observe(container);
    return () => observer.disconnect();
  }, [maxLines, safeText, t]);

  const truncated = maxLines ? lineTruncated : wordTruncated;
  const showMore = maxLines
    ? isLineOverflow
    : (!allEmpty || ismaxWordsExceeds) && (!isHubContent || !isShowMoreButton || isShowMoreIsmaxWordsExceeds);

  const handleMoreClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    showMoreClickHandler?.();
  };

  return (
    <div ref={containerRef} className={classNames(styles.collapsibleText, className)}>
      {maxLines ? (
        <div ref={measureRef} className={styles.dummyDiv} aria-hidden="true" />
      ) : null}
      <p className={styles.textContainer} style={textStyle}>
        <span>
          {truncated}
          {showMore && (
            <button type="button" className={styles.toggleText} onClick={handleMoreClick} style={{ color: themeConfig.primary }}>
              {t('more')}
            </button>
          )}
        </span>
      </p>
    </div>
  );
};

export default CollapsibleText;
